// ./src/components/features/EventShare/EventSharePanel.tsx
//
// ROADMAP #138 — THE share surface for a live event: pick which marketing channel a link is FOR (its `?ch=` link and
// QR), copy it, download the QR, or post it to Facebook, LinkedIn, X, WhatsApp, Telegram, Reddit or email. The
// wizard's finish screen and the dashboard's Share drawer both render this — it was the finish screen's own card.
//
// ⚠ ONE url drives everything on it — the QR, the shown and copied link, and every post — so none of them can disagree
//   about which channel it belongs to. The tracked link is the channels board's own builder (`buildChannelUrl`).
// ⚠ The posts are the PUBLIC SITE's share rules (`@em/blocks/shareTargets`, guarded by `check-share`): a hand-built
//   copy is how a WhatsApp post ends up with no link in it. The leaf, not the blocks barrel.
// ⚠ The channel picker is `MarketingChannelSelect` (full screen on a phone, "Add a new channel", its own read and
//   provider scoped by the event). `channels` is REQUIRED, so a caller cannot forget who may tag a link
//   (`mayTagWithChannels`); with it false no channel is read at all.
// ⓘ For a LIVE event only: a draft's link opens nothing, and a share surface over it reads as "go send this".

import React, { useState } from 'react';
import { Button, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { buildShareLinks, defaultShareText } from '@em/blocks/shareTargets';

import { AppIcon } from '@/components/generic';
import { buildChannelUrl } from '@/components/features/MarketingChannels/EventMarketingChannelsBoard/EventMarketingChannelsBoard';
import { MarketingChannelSelect } from '@/components/features/MarketingChannels/MarketingChannelSelect/MarketingChannelSelect';
import { Event, MarketingChannel } from '@/utils/api';

import { qrFileName } from './eventShareRules';
import { ShareQrLink } from './ShareQrLink';

export interface EventSharePanelProps {
  event: Event;
  /** The event's public link (`buildEventPublicUrl`). */
  baseUrl: string;
  /**
   * Offer the account's marketing channels, so the link is tagged with one. ⚠ REQUIRED — `mayTagWithChannels(crew)` on
   * an event page: crew without `event.operate` cannot read the event account's channels.
   */
  channels: boolean;
}

const PanelInner: React.FC<EventSharePanelProps> = ({ event, baseUrl, channels: offerChannels }) => {
  const { t } = useTranslation(['events', 'common']);
  const [channel, setChannel] = useState<MarketingChannel | null>(null);
  // ⚠ Only a channel picked while channels are OFFERED counts.
  const tag = offerChannels ? channel : null;
  const url = tag?.code ? buildChannelUrl(baseUrl, tag.code) : baseUrl;

  const title = event.title ?? '';
  const posts = buildShareLinks({ url, title, text: defaultShareText(title) });

  return (
    <Stack gap="md" w="100%">
      {offerChannels && event.id ? (
        <MarketingChannelSelect
          eventId={event.id}
          value={tag?.id ?? null}
          onChange={setChannel}
          hideWhenUnusable
          label={t('events:share.trackAs', { defaultValue: 'Which channel is this link for?' })}
          description={t('events:share.trackAsHint', {
            defaultValue: 'The link, QR code and posts below carry it, so you can see which one brings guests.',
          })}
          placeholder={t('events:share.noChannel', { defaultValue: 'No channel — the plain link' })}
        />
      ) : null}

      <ShareQrLink
        url={url}
        fileName={qrFileName(event.slug || event.id, tag?.code)}
        qrAlt={t('events:share.qrAlt', { defaultValue: 'Event QR code' })}
      />

      <Stack gap="xs">
        <Text fw={600} size="md" ta="left">
          {t('events:share.postTo', { defaultValue: 'Post it' })}
        </Text>
        <SimpleGrid cols={2} spacing="xs">
          {posts.map(({ network, href }) => (
            <Button
              key={network.id}
              component="a"
              href={href}
              target={network.id === 'email' ? undefined : '_blank'}
              rel="noopener noreferrer"
              variant="default"
              size="md"
              leftSection={<AppIcon iconName={network.icon} size={18} />}
            >
              {network.label}
            </Button>
          ))}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
};

// ⚠ Keyed by the event (#138 review): a channel picked for one event must not tag another's link.
export const EventSharePanel: React.FC<EventSharePanelProps> = (props) => <PanelInner key={props.event.id ?? ''} {...props} />;
